import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { getSettlementConfig } from 'services/apiSrv';
import styled from 'styled-components';
import { formatDate } from 'utils/helpers';
// ─── Styled Components ────────────────────────────────────────────────

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem 1rem;
`;

const Title = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  margin-bottom: 2rem;
  color: #111827;
`;

const Card = styled.div`
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  overflow: hidden;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(to right, #f9fafb, #ffffff);
`;

const CardIcon = styled.div`
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  border-radius: 0.5rem;
  overflow: hidden;
  background: #e5e7eb;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const CardTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const CardContent = styled.div`
  padding: 1.5rem;
`;

const DescriptionList = styled.dl`
  display: flex;
  flex-direction: column;
  gap: 1.125rem;
`;

const Term = styled.dt`
  font-size: 0.875rem;
  font-weight: 500;
  color: #6b7280;
`;

const Definition = styled.dd`
  margin: 0.25rem 0 0 0;
  font-size: 0.95rem;
  color: #111827;
  font-weight: ${props => (props.$bold ? '600' : '400')};
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 50vh;
`;

const Spinner = styled.div`
  border: 4px solid #e5e7eb;
  border-top: 4px solid #7c3aed;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.span`
  margin-left: 1.25rem;
  color: #4b5563;
  font-weight: 500;
`;

const ErrorMessage = styled.div`
  padding: 1.5rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 0.5rem;
  color: #991b1b;
`;

// ─── Main Component ────────────────────────────────────────────────────

function SettlementConfigDetails() {
  const { id: settlementConfigSystemRef } = useParams();

  const {
    data: config,
    isLoading,
    isError,
    error,
    isFetching,
  } = useQuery({
    queryKey: ['settlement-config', settlementConfigSystemRef],
    queryFn: () => getSettlementConfig(settlementConfigSystemRef).then(res => res.data),
    enabled: !!settlementConfigSystemRef,
    staleTime: 5 * 60 * 1000,   // 5 minutes
    gcTime: 15 * 60 * 1000,     // garbage collect after 15 min
    retry: 1,
  });

  if (!settlementConfigSystemRef) {
    return <ErrorMessage>No configuration reference provided in URL</ErrorMessage>;
  }

  if (isLoading || isFetching) {
    return (
      <LoaderContainer>
        <Spinner />
        <LoadingText>Loading configuration details...</LoadingText>
      </LoaderContainer>
    );
  }

  if (isError) {
    return (
      <ErrorMessage>
        Failed to load configuration: {error?.message || 'Unknown error'}
        <br />
        <small>Please try again later or contact support.</small>
      </ErrorMessage>
    );
  }

  if (!config) {
    return <ErrorMessage>No configuration found for this reference.</ErrorMessage>;
  }

  return (
    <PageContainer>
      <Title>Settlement Configuration Details</Title>

      <Card>
        <CardHeader>
          <CardIcon>
            <img
              src="https://cdn-images.toolify.ai/website-logos/20260114/1768370001_28551700_29086.webp"
              alt="Config Icon"
            />
          </CardIcon>
    
        </CardHeader>

        <CardContent>
          <DescriptionList>
            <div>
              <Term>Reference</Term>
              <Definition>{settlementConfigSystemRef}</Definition>
            </div>

            <div>
              <Term>Created At</Term>
              <Definition>{config.createdAt ? formatDate(config.createdAt): '—'}</Definition>
            </div>
            <div>
              <Term>Threshold</Term>
              <Definition>{config.settlementConfigThreshold ? config.settlementConfigThreshold : 0}</Definition>
            </div>

            <div>
              <Term>Account</Term>
              <Definition>{config.settlementConfigEntityAccount ? config.settlementConfigEntityAccount : '—'}</Definition>
            
            </div>

            <div>
              <Term>Frequency</Term>
              <Definition>{config.settlementConfigFrequency ? config.settlementConfigFrequency : '—'}</Definition>
            </div>

            <div>
              <Term>Channel</Term>
              <Definition>{config.settlementConfigChannel || '—'}</Definition>
            </div>



            {config.settlementConfigChannelDetails && Object.keys(config.settlementConfigChannelDetails).length > 0 && (
            <div>
                <Term>Channel Details</Term>
                <Definition>
                <DescriptionList style={{ marginTop: '0.5rem' }}>
                    {Object.entries(config.settlementConfigChannelDetails).map(([key, value]) => (
                    <div key={key}>
                        <dt style={{ 
                        color: '#6b7280', 
                        fontWeight: 500,
                        fontSize: '0.875rem'
                        }}>
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                        </dt>
                        <dd style={{ 
                        margin: '0.125rem 0 0.75rem 1rem', 
                        color: '#111827',
                        fontSize: '0.95rem'
                        }}>
                        {String(value)}
                        </dd>
                    </div>
                    ))}
                </DescriptionList>
                </Definition>
            </div>
            )}
          </DescriptionList>
        </CardContent>
      </Card>
    </PageContainer>
  );
}

export default SettlementConfigDetails;